import React, { useState, useEffect } from 'react';
import {
  CreditCard, Search, Filter, Download, Eye, CheckCircle,
  Clock, AlertCircle, Plus, Calendar, DollarSign, Building2,
  X, Phone, Mail, MapPin, FileText, History, TrendingUp, User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AccountsPayableTab = () => {
  const { language, t } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Listen for event to open payment form
  useEffect(() => {
    const handleOpenPaymentForm = () => {
      setShowPaymentForm(true);
    };
    window.addEventListener('openPaymentForm', handleOpenPaymentForm);
    return () => window.removeEventListener('openPaymentForm', handleOpenPaymentForm);
  }, []);

  const vendors = [
    {
      id: 'V-001',
      name: 'ABC Supplies Co.',
      balance: 450000,
      current: 250000,
      days30: 150000,
      days60: 50000,
      days90: 0,
      lastPayment: '2024-02-05',
      status: 'active'
    },
    {
      id: 'V-002',
      name: 'XYZ Manufacturing',
      balance: 320000,
      current: 180000,
      days30: 100000,
      days60: 40000,
      days90: 0,
      lastPayment: '2024-02-08',
      status: 'active'
    },
    {
      id: 'V-003',
      name: 'Global Logistics',
      balance: 189000,
      current: 120000,
      days30: 50000,
      days60: 19000,
      days90: 0,
      lastPayment: '2024-02-01',
      status: 'active'
    },
    {
      id: 'V-004',
      name: 'Tech Solutions Inc.',
      balance: 125000,
      current: 80000,
      days30: 30000,
      days60: 15000,
      days90: 0,
      lastPayment: '2024-01-28',
      status: 'active'
    },
    {
      id: 'V-005',
      name: 'Office Supplies Ltd.',
      balance: 89000,
      current: 60000,
      days30: 20000,
      days60: 9000,
      days90: 0,
      lastPayment: '2024-02-10',
      status: 'active'
    }
  ];

  const invoices = [
    {
      id: 'INV-AP-2024-001',
      vendor: 'ABC Supplies Co.',
      vendorId: 'V-001',
      invoiceNumber: 'INV-ABC-1234',
      date: '2024-02-10',
      dueDate: '2024-03-10',
      amount: 125000,
      paid: 0,
      balance: 125000,
      status: 'open',
      daysOverdue: 0,
      aging: 'current'
    },
    {
      id: 'INV-AP-2024-002',
      vendor: 'XYZ Manufacturing',
      vendorId: 'V-002',
      invoiceNumber: 'INV-XYZ-5678',
      date: '2024-01-25',
      dueDate: '2024-02-24',
      amount: 85000,
      paid: 0,
      balance: 85000,
      status: 'open',
      daysOverdue: 18,
      aging: '1-30'
    },
    {
      id: 'INV-AP-2024-003',
      vendor: 'Global Logistics',
      vendorId: 'V-003',
      invoiceNumber: 'INV-GL-9012',
      date: '2024-01-15',
      dueDate: '2024-02-14',
      amount: 45000,
      paid: 0,
      balance: 45000,
      status: 'open',
      daysOverdue: 28,
      aging: '31-60'
    },
    {
      id: 'INV-AP-2024-004',
      vendor: 'Tech Solutions Inc.',
      vendorId: 'V-004',
      invoiceNumber: 'INV-TS-3456',
      date: '2024-01-05',
      dueDate: '2024-02-04',
      amount: 32000,
      paid: 0,
      balance: 32000,
      status: 'open',
      daysOverdue: 38,
      aging: '31-60'
    },
    {
      id: 'INV-AP-2024-005',
      vendor: 'Office Supplies Ltd.',
      vendorId: 'V-005',
      invoiceNumber: 'INV-OS-7890',
      date: '2023-12-20',
      dueDate: '2024-01-19',
      amount: 15000,
      paid: 0,
      balance: 15000,
      status: 'open',
      daysOverdue: 53,
      aging: '61-90'
    }
  ];

  const totalPayables = vendors.reduce((sum, v) => sum + v.balance, 0);
  const overdueAmount = invoices
    .filter(inv => inv.daysOverdue > 0)
    .reduce((sum, inv) => sum + inv.balance, 0);

  const getAgingColor = (aging) => {
    switch (aging) {
      case 'current': return 'text-green-600 bg-green-50';
      case '1-30': return 'text-yellow-600 bg-yellow-50';
      case '31-60': return 'text-orange-600 bg-orange-50';
      case '61-90': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Get vendor details (mock data)
  const getVendorDetails = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (!vendor) return null;

    return {
      ...vendor,
      contact: {
        name: 'Ahmed Al-Saud',
        email: 'contact@' + vendor.name.toLowerCase().replace(/\s+/g, '') + '.com',
        phone: '+966 50 123 4567',
        address: 'King Fahd Road, Riyadh 12345, Saudi Arabia'
      },
      totalPurchases: vendor.balance * 3.5, // Mock calculation
      purchaseHistory: invoices.filter(inv => inv.vendorId === vendorId).map(inv => ({
        ...inv,
        purchaseDate: inv.date,
        items: [
          { description: 'Office Supplies', quantity: 100, unitPrice: inv.amount / 100 }
        ]
      })),
      files: [
        { id: '1', name: 'Contract_2024.pdf', type: 'pdf', date: '2024-01-15', size: '2.5 MB' },
        { id: '2', name: 'Tax_Certificate.pdf', type: 'pdf', date: '2024-01-10', size: '1.2 MB' },
        { id: '3', name: 'Bank_Details.pdf', type: 'pdf', date: '2024-01-05', size: '0.8 MB' }
      ],
      paymentHistory: [
        { date: '2024-02-05', amount: 250000, method: 'Bank Transfer', reference: 'PAY-2024-001' },
        { date: '2024-01-15', amount: 180000, method: 'Check', reference: 'CHK-2024-001' },
        { date: '2024-01-01', amount: 320000, method: 'Bank Transfer', reference: 'PAY-2023-050' }
      ]
    };
  };

  const handleVendorClick = (vendor) => {
    const details = getVendorDetails(vendor.id);
    setSelectedVendor(details);
    setShowVendorModal(true);
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    if (filter === 'overdue') return inv.daysOverdue > 0;
    if (filter === 'current') return inv.aging === 'current';
    return inv.aging === filter;
  });

  return (
    <div className={`space-y-6 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('totalPayables')}</p>
            <CreditCard className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">SAR {totalPayables.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('overdue')}</p>
            <AlertCircle className="h-5 w-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-600">SAR {overdueAmount.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('current')}</p>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-600">
            SAR {(totalPayables - overdueAmount).toLocaleString()}
          </p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('vendors')}</p>
            <Building2 className="h-5 w-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{vendors.length}</p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 py-2 ${
                language === 'ar' ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'
              }`}
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white ${language === 'ar' ? 'text-right' : 'text-left'}`}
          >
            <option value="all">{t('allInvoices')}</option>
            <option value="current">{t('current')}</option>
            <option value="1-30">{language === 'ar' ? '1-30 يوم' : '1-30 Days'}</option>
            <option value="31-60">{language === 'ar' ? '31-60 يوم' : '31-60 Days'}</option>
            <option value="61-90">{language === 'ar' ? '61-90 يوم' : '61-90 Days'}</option>
            <option value="overdue">{t('overdue')}</option>
          </select>
        </div>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={() => alert(t('exportFunctionality'))}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </button>
          <button 
            onClick={() => setShowPaymentForm(true)}
            className={`px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Plus className="h-4 w-4" />
            {t('recordPayment')}
          </button>
        </div>
      </div>

      {/* Vendors Summary */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className={`p-5 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <h3 className="text-lg font-semibold text-gray-900">{t('vendorSummary')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <thead>
              <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('vendor')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('totalBalance')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('current')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('days30')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('days60')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('days90')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('lastPayment')}</th>
                <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((vendor) => (
                <tr key={vendor.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900">{vendor.name}</p>
                      <p className="text-xs text-gray-500">{vendor.id}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-semibold text-gray-900">
                    SAR {vendor.balance.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-700">
                    SAR {vendor.current.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-700">
                    SAR {vendor.days30.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-700">
                    SAR {vendor.days60.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-700">
                    SAR {vendor.days90.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-gray-600">{vendor.lastPayment}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => handleVendorClick(vendor)}
                        className="p-1.5 hover:bg-gray-200 rounded"
                      >
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className={`p-5 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <h3 className="text-lg font-semibold text-gray-900">{t('outstandingInvoices')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <thead>
              <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('invoiceNumber')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('vendor')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('date')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dueDate')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('amount')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('balance')}</th>
                <th className={`py-3 px-4 text-center`}>{t('aging')}</th>
                <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <div>
                      <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                      <p className="text-xs text-gray-500">{invoice.id}</p>
                    </div>
                  </td>
                  <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{invoice.vendor}</td>
                  <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{invoice.date}</td>
                  <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{invoice.dueDate}</td>
                  <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {invoice.amount.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 font-semibold text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {invoice.balance.toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getAgingColor(invoice.aging)}`}>
                        {invoice.daysOverdue > 0 ? `${invoice.daysOverdue} ${t('days')}` : invoice.aging}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      invoice.status === 'open' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {invoice.status === 'open' ? t('open') : t('paid')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => {
                          const vendor = vendors.find(v => v.id === invoice.vendorId);
                          if (vendor) handleVendorClick(vendor);
                        }}
                        className="p-1.5 hover:bg-gray-200 rounded"
                      >
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                      {invoice.status === 'open' && (
                        <button 
                          onClick={() => {
                            setShowPaymentForm(true);
                            setSelectedVendor({ id: invoice.vendorId, name: invoice.vendor, invoiceId: invoice.id });
                          }}
                          className="p-1.5 hover:bg-blue-100 rounded"
                        >
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vendor Detail Modal */}
      <AnimatePresence>
        {showVendorModal && selectedVendor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVendorModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className={`p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedVendor.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">{t('vendorID')}: {selectedVendor.id}</p>
                  </div>
                  <button
                    onClick={() => setShowVendorModal(false)}
                    className="p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 overflow-y-auto p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className={`bg-blue-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('outstandingBalance')}</p>
                    <p className="text-2xl font-bold text-blue-600">
                      SAR {selectedVendor.balance.toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-green-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('totalPurchases')}</p>
                    <p className="text-2xl font-bold text-green-600">
                      SAR {selectedVendor.totalPurchases.toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-purple-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('openInvoices')}</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {selectedVendor.purchaseHistory.length}
                    </p>
                  </div>
                  <div className={`bg-orange-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('lastPayment')}</p>
                    <p className="text-lg font-bold text-orange-600">
                      {selectedVendor.lastPayment}
                    </p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <User className="h-5 w-5" />
                    {t('contactInformation')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <User className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                          <p className="text-sm text-gray-600">{t('contactPerson')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedVendor.contact.name}</p>
                        </div>
                      </div>
                      <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                          <p className="text-sm text-gray-600">{t('email')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedVendor.contact.email}</p>
                        </div>
                      </div>
                      <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                          <p className="text-sm text-gray-600">{t('phone')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedVendor.contact.phone}</p>
                        </div>
                      </div>
                      <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                          <p className="text-sm text-gray-600">{t('address')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedVendor.contact.address}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Purchase History */}
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <History className="h-5 w-5" />
                    {t('purchaseHistory')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('invoiceNumber')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('date')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dueDate')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('amount')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('balance')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 text-center`}>{t('status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVendor.purchaseHistory.map((purchase) => (
                          <tr key={purchase.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                              <button className="text-blue-600 hover:text-blue-700 font-medium">
                                {purchase.invoiceNumber}
                              </button>
                            </td>
                            <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{purchase.purchaseDate}</td>
                            <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{purchase.dueDate}</td>
                            <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              SAR {purchase.amount.toLocaleString()}
                            </td>
                            <td className={`py-3 px-4 font-semibold text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              SAR {purchase.balance.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                purchase.status === 'open' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {purchase.status === 'open' ? t('open') : t('paid')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment History */}
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <DollarSign className="h-5 w-5" />
                    {t('paymentHistory')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('date')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('amount')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('method')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('reference')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVendor.paymentHistory.map((payment, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{payment.date}</td>
                            <td className={`py-3 px-4 font-semibold text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              SAR {payment.amount.toLocaleString()}
                            </td>
                            <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{payment.method}</td>
                            <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                              <button className="text-blue-600 hover:text-blue-700 font-medium">
                                {payment.reference}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Files */}
                <div>
                  <h3 className={`text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <FileText className="h-5 w-5" />
                    {t('filesDocuments')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="space-y-2">
                      {selectedVendor.files.map((file) => (
                        <div key={file.id} className={`flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                          <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{file.size} • {file.date}</p>
                            </div>
                          </div>
                          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                            {t('download')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                <button
                  onClick={() => setShowVendorModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  {t('close')}
                </button>
                <button
                  onClick={() => {
                    setShowVendorModal(false);
                    setShowPaymentForm(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  {t('recordPayment')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Form Modal */}
      <AnimatePresence>
        {showPaymentForm && (
          <PaymentFormModal
            isOpen={showPaymentForm}
            onClose={() => {
              setShowPaymentForm(false);
              setSelectedVendor(null);
            }}
            vendor={selectedVendor}
            vendors={vendors}
            invoices={invoices}
            onSave={(payment) => {
              console.log('Saving payment:', payment);
              setShowPaymentForm(false);
              setSelectedVendor(null);
              alert(t('paymentRecordedSuccessfully'));
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Payment Form Modal Component
const PaymentFormModal = ({ isOpen, onClose, vendor, vendors, invoices, onSave }) => {
  const { language, t } = useLanguage();
  const [showSupplierInfo, setShowSupplierInfo] = useState(false);
  const [formData, setFormData] = useState({
    vendorId: vendor?.id || '',
    invoiceId: vendor?.invoiceId || '',
    date: new Date().toISOString().split('T')[0],
    amount: vendor?.invoiceId ? invoices.find(inv => inv.id === vendor.invoiceId)?.balance || '' : '',
    paymentMethod: 'Bank Transfer',
    reference: '',
    notes: '',
    poNumber: '',
    supplierReference: '',
    deliveryNote: '',
    grnNumber: ''
  });

  if (!isOpen) return null;

  const selectedVendorInvoices = invoices.filter(inv => inv.vendorId === formData.vendorId && inv.status === 'open');

  const handleSave = () => {
    if (!formData.vendorId || !formData.amount || !formData.date) {
      alert(t('pleaseFillAllFields'));
      return;
    }
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className={`p-6 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <h2 className="text-2xl font-bold text-gray-900">{t('recordPayment')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('vendor')} *</label>
            <select
              value={formData.vendorId}
              onChange={(e) => {
                setFormData({ ...formData, vendorId: e.target.value, invoiceId: '' });
              }}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            >
              <option value="">{t('selectVendor')}</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>

          {formData.vendorId && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('invoiceOptional')}</label>
              <select
                value={formData.invoiceId}
                onChange={(e) => {
                  const invoice = invoices.find(inv => inv.id === e.target.value);
                  setFormData({ 
                    ...formData, 
                    invoiceId: e.target.value,
                    amount: invoice ? invoice.balance : formData.amount
                  });
                }}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              >
                <option value="">{t('selectInvoiceOrLeaveBlank')}</option>
                {selectedVendorInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} - {t('balanceSAR')} {inv.balance.toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('paymentDate')} *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('amountSAR')} *</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('paymentMethod')}</label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              >
                <option value={t('bankTransfer')}>{t('bankTransfer')}</option>
                <option value={t('check')}>{t('check')}</option>
                <option value={t('cash')}>{t('cash')}</option>
                <option value={t('creditCard')}>{t('creditCard')}</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('referenceNumber')}</label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="PAY-2024-001"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={t('additionalNotes')}
              rows={3}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            />
          </div>

          {/* Supplier Information Toggle */}
          <div className="border-t border-gray-200 pt-4">
            <div className={`flex items-center justify-between mb-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                <h3 className="text-sm font-semibold text-gray-900">{t('supplierInformation')}</h3>
                <p className="text-xs text-gray-500 mt-1">{language === 'ar' ? 'اختياري: أضف رقم أمر الشراء وتفاصيل المورد الأخرى' : 'Optional: Add PO number and other supplier details'}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showSupplierInfo}
                  onChange={(e) => setShowSupplierInfo(e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-blue-600 ${
                  language === 'ar' 
                    ? 'peer-checked:after:-translate-x-full after:right-[2px] after:left-auto' 
                    : 'peer-checked:after:translate-x-full after:left-[2px]'
                } after:content-[''] after:absolute after:top-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
              </label>
            </div>

            {showSupplierInfo && (
              <div className={`space-y-4 bg-gray-50 p-4 rounded-lg ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('poNumber')}</label>
                    <input
                      type="text"
                      value={formData.poNumber}
                      onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                      placeholder="PO-2024-001"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('supplierReference')}</label>
                    <input
                      type="text"
                      value={formData.supplierReference}
                      onChange={(e) => setFormData({ ...formData, supplierReference: e.target.value })}
                      placeholder={language === 'ar' ? 'رقم فاتورة/مرجع المورد' : 'Supplier invoice/ref number'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('deliveryNote')}</label>
                    <input
                      type="text"
                      value={formData.deliveryNote}
                      onChange={(e) => setFormData({ ...formData, deliveryNote: e.target.value })}
                      placeholder="DN-2024-001"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('grnNumber')}</label>
                    <input
                      type="text"
                      value={formData.grnNumber}
                      onChange={(e) => setFormData({ ...formData, grnNumber: e.target.value })}
                      placeholder="GRN-2024-001"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            {t('recordPayment')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AccountsPayableTab;
